import { RefObject, useCallback, useEffect, useRef } from 'react'
import { useBlocker, useLocation, useNavigate } from 'react-router'

interface OnBeforeLeavePromptFunctionProps {
  silent: boolean
  allow: () => void
  reject: () => void
}

export interface OnBeforeLeavePromptFunction {
  (props: OnBeforeLeavePromptFunctionProps): void
}

export function useBeforeLeaveGuard(
  onBeforeLeave: OnBeforeLeavePromptFunction,
  isBlocked: boolean,
  skipBlockRef?: RefObject<boolean>,
) {
  const location = useLocation()
  const navigate = useNavigate()
  const isBlockedRef = useRef(isBlocked)
  isBlockedRef.current = isBlocked

  const shouldBlock = useCallback(
    () => !skipBlockRef?.current && isBlockedRef.current,
    [skipBlockRef],
  )

  const blocker = useBlocker(shouldBlock)
  const blockerRef = useRef(blocker)
  blockerRef.current = blocker

  useEffect(() => {
    if (
      location?.key !== blocker?.location?.key &&
      blocker.state === 'blocked'
    ) {
      const targetLocation = blocker.location

      onBeforeLeave({
        silent:
          location.pathname === blocker?.location.pathname &&
          location.search !== blocker?.location.search,
        allow: () => {
          try {
            if (blockerRef.current.state === 'blocked') {
              blockerRef.current.proceed()
              return
            }
          } catch {}

          if (targetLocation) {
            navigate(targetLocation, { replace: true })
          }
        },
        reject: () => {
          if (blockerRef.current.state === 'blocked') {
            blockerRef.current.reset()
          }
        },
      })
    }
  }, [
    blocker,
    location?.key,
    location.pathname,
    location.search,
    navigate,
    onBeforeLeave,
  ])
}
